import {
  fortschritt,
  fragenzaehler,
  naechsterSchritt,
  type Antworten,
  type Funnel,
} from "@fbs/funnel-schema";
import { useCallback, useEffect, useMemo, useRef, useState } from "preact/hooks";

import { absendedatenBauen } from "./absenden";
import { ApiFehler, leadSenden } from "./api";
import { Abschluss, Lader } from "./Abschluss";
import { SchrittAnsicht } from "./schritte";
import { kontaktBeschriftung } from "./schritte/gemeinsam";
import { standLoeschen, standSpeichern, type Sitzungsstand } from "./speicher";
import { Tracker } from "./tracking";
import { teileVon, useFunnel } from "./zustand";

interface Props {
  funnel: Funnel;
  slug: string;
  stand: Sitzungsstand | null;
  /**
   * Antworten, die schon vor dem ersten Bildschirm feststehen.
   *
   * Heute genau eine: die Postleitzahl, die auf der Startseite zusammen mit
   * dem Gewerk abgefragt wurde. Sie wird eingetragen, nicht übersprungen - wer
   * sich vertippt hat, soll es hier noch sehen und ändern können.
   */
  vorbelegung?: Antworten;
}

/**
 * Die Kennung des Bildschirms, nicht des Schritts.
 *
 * Ein Kontaktschritt mit vier Feldern hat vier Bildschirme, und ein Abbruch
 * beim Telefonfeld ist etwas anderes als einer beim Vornamen.
 *
 * Diese Funktion steht hier und wird von allen Meldungen benutzt, weil eine
 * uneinheitliche Kennung die Auswertung unbrauchbar macht: Meldet "gesehen"
 * den Bildschirm und "weiter" den Schritt, findet die Analyse zu jedem
 * Bildschirm ein Erreichen ohne Weitergehen und weist hundert Prozent Absprung
 * aus, wo in Wirklichkeit alle weitergegangen sind.
 */
function bildschirmKennung(schrittId: string, teil: number | null): string {
  return teil === null ? schrittId : `${schrittId}#${String(teil + 1)}`;
}

/**
 * Die Hülle um die Fragen.
 *
 * Sie kennt keinen einzigen Fragetext. Was sie tut: den Fortschritt zeigen,
 * vor und zurück schalten, den Stand sichern und am Ende absenden. Alles
 * Inhaltliche kommt aus dem Schema, alles Fragetypische aus schritte/.
 */
export function FunnelAnsicht({ funnel, slug, stand, vorbelegung = {} }: Props) {
  const steuerung = useFunnel(funnel, stand, vorbelegung);
  const { zustand, schritt } = steuerung;

  const honigtopf = useRef<HTMLInputElement>(null);
  const ueberschrift = useRef<HTMLHeadingElement>(null);
  const wurzel = useRef<HTMLDivElement>(null);
  /** Beim ersten Bild wird nicht gescrollt - siehe Bildschirmwechsel. */
  const ersterAufbau = useRef(true);

  /**
   * Die Erfassung.
   *
   * Wird genau einmal erzeugt, nicht bei jedem Rendern. Ein zweiter Tracker
   * würde eine zweite Sitzung anlegen, und aus einem Besucher würden in der
   * Auswertung zwei.
   */
  const trackerRef = useRef<Tracker | null>(null);
  trackerRef.current ??= new Tracker(slug, funnel.settings.tracking.batch_interval_ms);
  const tracker = trackerRef.current;

  useEffect(() => {
    tracker.starten();
    tracker.melden("funnel_view");
    // Eine fortgesetzte Sitzung ist etwas anderes als ein Erstbesuch: Sie
    // beginnt nicht bei null und darf die Abbruchquote nicht verfälschen.
    tracker.melden(stand !== null ? "funnel_resumed" : "funnel_started");
    // Absichtlich nur beim ersten Aufbau, mit leerer Abhängigkeitsliste: Der
    // Tracker lebt so lange wie die Seite, nicht so lange wie ein Rendern.
    // Ein zweiter Lauf würde eine zweite Sitzung melden.
  }, []);

  /**
   * Zeigt immer auf die aktuelle Fassung von weiterGehen.
   *
   * Der Zeitgeber des automatischen Weitergehens würde sonst die Fassung
   * benutzen, die galt, als er gestartet wurde, also ohne die gerade
   * angetippte Antwort. Zugewiesen wird weiter unten, sobald weiterGehen
   * existiert.
   */
  const weiterRef = useRef<() => void>(() => undefined);

  /** Dasselbe für den Rückweg - gebraucht, um Zwischenseiten zu überspringen. */
  const zurueckRef = useRef<() => void>(() => undefined);

  const autoTimer = useRef<number | null>(null);

  const autoWeiter = useCallback(() => {
    if (!funnel.settings.navigation.auto_advance_single_choice) return;
    if (autoTimer.current !== null) window.clearTimeout(autoTimer.current);
    autoTimer.current = window.setTimeout(() => {
      autoTimer.current = null;
      weiterRef.current();
    }, funnel.settings.navigation.auto_advance_delay_ms);
  }, [funnel.settings.navigation]);

  // Ein laufender Timer, der nach dem Verlassen der Seite noch feuert, würde
  // auf einem abgebauten Baum arbeiten.
  useEffect(
    () => () => {
      if (autoTimer.current !== null) window.clearTimeout(autoTimer.current);
    },
    [],
  );

  // ------------------------------------------------------------------
  // Stand sichern
  // ------------------------------------------------------------------

  useEffect(() => {
    if (!funnel.settings.persistence.resume_enabled) return;
    if (zustand.phase !== "frage") return;

    standSpeichern(funnel.id, {
      aktuell: zustand.aktuell,
      antworten: zustand.antworten,
      besucht: zustand.besucht,
      begonnenAm: zustand.begonnenAm,
      gespeichertAm: Date.now(),
    });
  }, [
    funnel.id,
    funnel.settings.persistence.resume_enabled,
    zustand.aktuell,
    zustand.antworten,
    zustand.besucht,
    zustand.begonnenAm,
    zustand.phase,
  ]);

  // ------------------------------------------------------------------
  // Bildschirmwechsel
  // ------------------------------------------------------------------

  useEffect(() => {
    /*
     * Die Karte in den Blick holen - nicht die Seite an den Anfang.
     *
     * Hier stand window.scrollTo({ top: 0 }). Auf der eigenen Funnelseite war
     * das richtig: Dort steht der Funnel ganz oben. Eingebettet unter einem
     * Hero ist es das Gegenteil von dem, was gemeint war - gemessen auf einem
     * Telefon mit 375 x 812: Wer zum Quiz scrollt (241), eine Antwort antippt
     * und danach wieder bei 0 landet, steht über der Überschrift und muss vor
     * jeder Frage von Neuem herunterscrollen. Bei acht Fragen achtmal.
     *
     * Jetzt wandert die Karte an den oberen Rand. Der Abstand zur klebenden
     * Kopfzeile der Seite steckt als scroll-margin-top im Stylesheet, weil
     * nur die einbettende Seite weiß, wie hoch ihre Kopfzeile ist.
     *
     * Beim ersten Bild wird nicht gescrollt. Sonst risse die Seite dem
     * Besucher den Hero unter den Augen weg, bevor er ihn gelesen hat - und
     * der Hero ist es, der die Anzeige einlöst.
     */
    if (ersterAufbau.current) {
      ersterAufbau.current = false;
    } else {
      wurzel.current?.scrollIntoView({ block: "start", behavior: "instant" });
    }

    // Der Screenreader soll die neue Frage vorlesen, nicht den Anfang der
    // Seite. Ohne das bleibt der Vorlesepunkt beim Zurück-Knopf stehen.
    ueberschrift.current?.focus({ preventScroll: true });

    if (zustand.phase !== "frage") return;

    tracker.schrittGesehen(
      bildschirmKennung(zustand.aktuell, zustand.teil),
      zustand.besucht.length - 1,
    );
  }, [zustand.aktuell, zustand.teil, zustand.phase, zustand.besucht.length, tracker]);

  /**
   * Zwischenseiten, die von selbst weitergehen.
   *
   * Gedacht für den Moment nach der Adresse: ein Ladekreis und "Wir suchen
   * einen Betrieb in Ihrer Nähe", dann geht es weiter zu den Kontaktdaten. Die
   * Wartezeit ist keine vorgetäuschte - im Hintergrund läuft die
   * Gebietsprüfung - und sie gibt dem Ausfüllenden einen Grund, warum es
   * gleich um seine Daten geht.
   *
   * Rückwärts wird übersprungen. Wer von den Kontaktdaten zurückgeht, landete
   * sonst auf dem Ladekreis und würde sofort wieder nach vorn geschoben: eine
   * Seite, die sich nicht verlassen lässt.
   */
  useEffect(() => {
    if (zustand.phase !== "frage") return;
    if (schritt?.type !== "information") return;

    const dauer = schritt.auto_advance_after_ms;
    if (dauer === null) return;

    if (zustand.richtung === -1) {
      zurueckRef.current();
      return;
    }

    const uhr = window.setTimeout(() => {
      weiterRef.current();
    }, dauer);

    return () => {
      window.clearTimeout(uhr);
    };
  }, [zustand.aktuell, zustand.phase, zustand.richtung, schritt]);

  // ------------------------------------------------------------------
  // Absenden
  // ------------------------------------------------------------------

  const [gesendet, setGesendet] = useState(false);

  useEffect(() => {
    if (zustand.phase !== "senden" || gesendet) return;
    setGesendet(true);

    const daten = absendedatenBauen(funnel, zustand.antworten);
    tracker.melden("lead_submitted");

    void (async () => {
      const ladeDauer = funnel.completion.loader.enabled
        ? funnel.completion.loader.duration_ms
        : 0;
      // Der Lader und die Anfrage laufen nebeneinander. Er ist keine
      // vorgetäuschte Wartezeit: Die Anfrage braucht wirklich einen Moment,
      // und ist sie schneller, bleibt der Lader trotzdem lange genug stehen,
      // dass der Wechsel nicht flackert.
      const mindestens = new Promise((fertig) => window.setTimeout(fertig, ladeDauer));

      try {
        const [ergebnis] = await Promise.all([
          leadSenden({
            ...(funnel.settings.absenden.ziel === "lead"
              ? {}
              : { ziel: funnel.settings.absenden.ziel }),
            funnelSlug: slug,
            // Ohne diese Kennung hätte der Lead keine Herkunft: Die
            // Zuordnung zur Anzeige liegt an der Sitzung, nicht an der
            // Anfrage. Siehe modules/tracking/attribution.ts.
            sessionId: tracker.sessionId,
            antworten: zustand.antworten,
            kontakt: daten.kontakt,
            postcode: daten.postcode,
            city: daten.city,
            street: daten.street,
            consent: daten.consent,
            dauerMs: Date.now() - zustand.begonnenAm,
            honeypot: honigtopf.current?.value ?? "",
          }),
          mindestens,
        ]);

        standLoeschen(funnel.id);
        tracker.melden("funnel_completed", { payload: { reference: ergebnis.referenz } });
        // Schickt das letzte Bündel sofort ab und schaltet ab. Sonst läge das
        // wichtigste Ereignis noch in der Warteschlange, wenn der Besucher die
        // Danke-Seite wieder verlässt.
        tracker.beenden();

        /**
         * Auf eine eigene Danke-Seite weiterleiten, wenn eine hinterlegt ist.
         *
         * Für Google Ads ist das der entscheidende Punkt: Eine Conversion
         * lässt sich am einfachsten an einer eigenen Adresse messen. Solange
         * der Abschluss an derselben Adresse erscheint wie das Formular, gibt
         * es keinen Seitenaufruf, den man zählen könnte.
         *
         * Die Referenz reist mit. Ohne sie stünde auf der Danke-Seite keine
         * Vorgangsnummer, und genau danach fragt jeder, der später anruft.
         *
         * Vor dem Zustandswechsel und mit return: Meldeten wir hier erst
         * "fertig", würde die eigene Abschluss-Ansicht gezeichnet und im
         * nächsten Moment von der Weiterleitung wieder ersetzt. Genau das
         * war als kurzes Aufblitzen zu sehen. Es gibt keinen Grund, eine
         * Ansicht aufzubauen, die niemand lesen soll.
         */
        if (funnel.completion.redirect_url) {
          const ziel = new URL(funnel.completion.redirect_url, window.location.href);
          if (ergebnis.referenz) ziel.searchParams.set("ref", ergebnis.referenz);
          window.location.href = ziel.toString();
          return;
        }

        // Ohne hinterlegte Danke-Seite bleibt der Abschluss im Funnel.
        steuerung.fertigMelden(ergebnis.referenz);
      } catch (fehler) {
        await mindestens;
        setGesendet(false);
        steuerung.abbrechen(
          fehler instanceof ApiFehler
            ? fehler.message
            : "Die Anfrage konnte nicht gesendet werden. Bitte die Verbindung prüfen und es noch einmal versuchen.",
        );
      }
    })();
  }, [
    zustand.phase,
    zustand.antworten,
    zustand.begonnenAm,
    gesendet,
    funnel,
    slug,
    steuerung,
  ]);

  // ------------------------------------------------------------------
  // Darstellung
  // ------------------------------------------------------------------

  const stil = useMemo(
    () => ({
      "--f-primaer": funnel.theme.primary,
      "--f-aktion": funnel.theme.action ?? funnel.theme.primary,
      "--f-primaer-kontrast": funnel.theme.primary_contrast,
      "--f-akzent": funnel.theme.accent,
      "--f-flaeche": funnel.theme.surface,
      "--f-text": funnel.theme.text,
      "--f-radius": `${String(funnel.theme.radius)}px`,
    }),
    [funnel.theme],
  );

  if (zustand.phase === "senden") {
    return (
      <div class="f-wurzel" style={stil} ref={wurzel}>
        <Lader loader={funnel.completion.loader} />
      </div>
    );
  }

  if (zustand.phase === "fertig") {
    return (
      <div class="f-wurzel" style={stil} ref={wurzel}>
        <Abschluss abschluss={funnel.completion} referenz={zustand.referenz} />
      </div>
    );
  }

  if (!schritt) {
    return (
      <div class="f-wurzel" style={stil} ref={wurzel}>
        <p class="f-fehler">Dieser Funnel hat keine Fragen.</p>
      </div>
    );
  }

  const teile = teileVon(schritt);
  const letzterTeil =
    teile === null || zustand.teil === null || zustand.teil === teile - 1;

  // Ein Schritt, der sich auf mehrere Bildschirme verteilt, bewegt den Balken
  // auch zwischendrin. Siehe fortschritt() in @fbs/funnel-schema.
  const anteil = teile === null || zustand.teil === null ? 1 : (zustand.teil + 1) / teile;
  const prozent = fortschritt(funnel, zustand.aktuell, zustand.antworten, anteil);

  /*
   * "Frage 2 von 5" statt "40 %", wenn der Funnel es will.
   *
   * Auf Zwischenseite und Kontaktdaten gibt es keine Fragennummer - dort
   * steht "Fast geschafft", denn genau dort springen die meisten ab.
   */
  const zaehler = funnel.settings.progress.show_step_numbers
    ? fragenzaehler(funnel, zustand.aktuell, zustand.antworten)
    : null;
  const zeithinweis = funnel.settings.progress.duration_hint;
  const fortschrittText =
    zaehler === null
      ? `${String(prozent)} %`
      : zaehler.nummer === null
        ? "Fast geschafft"
        : `Frage ${String(zaehler.nummer)} von ${String(zaehler.gesamt)}${
            zaehler.nummer === 1 && zeithinweis ? ` · ${zeithinweis}` : ""
          }`;
  const istLetzter =
    letzterTeil && naechsterSchritt(funnel, zustand.aktuell, zustand.antworten) === null;

  /**
   * Bei einer Einfachauswahl trägt der Tipp auf die Antwort schon weiter. Ein
   * zusätzlicher Weiter-Knopf darunter macht aus einem Tipp zwei und ist der
   * häufigste Grund, warum sich ein Funnel zäh anfühlt. Er erscheint nur, wenn
   * die Frage übersprungen werden darf.
   */
  const autoTraegt =
    schritt.type === "single_choice" &&
    funnel.settings.navigation.auto_advance_single_choice;
  /*
   * Kein Knopf auf einer Seite, die von selbst weitergeht. Ein Knopf, der
   * gleich verschwindet, lädt zum Tippen ein und tut dann nichts.
   */
  const gehtVonSelbst =
    schritt.type === "information" && schritt.auto_advance_after_ms !== null;

  const knopfZeigen = (!autoTraegt || !schritt.required) && !gehtVonSelbst;

  const knopfText = autoTraegt
    ? "Überspringen"
    : schritt.type === "information"
      ? schritt.cta_label
      : schritt.type === "contact" && istLetzter && schritt.cta_label !== undefined
        ? schritt.cta_label
        : istLetzter
          ? "Anfrage absenden"
          : "Weiter";

  /*
   * Die kleine Zeile im Knopf.
   *
   * "kostenlos & unverbindlich" gehoert an die Stelle, an der jemand zoegert,
   * und das ist der Knopf - nicht eine Zeile weiter oben, die beim Tippen
   * schon aus dem Blick ist.
   */
  const knopfZusatz =
    schritt.type === "contact" && istLetzter ? schritt.cta_note : undefined;

  /**
   * Ein geteilter Kontaktschritt bekommt je Bildschirm eine eigene Frage.
   *
   * Stünde auf allen Bildschirmen dieselbe, sähe es aus, als sei nichts
   * passiert. Steht im Funnel keine eigene Frage, wird auf die Beschriftung
   * des Feldes ausgewichen und die gemeinsame Frage rückt als kleine Zeile
   * darüber, damit der Zusammenhang nicht verlorengeht.
   */
  const kontaktbildschirm =
    schritt.type === "contact" && zustand.teil !== null
      ? (schritt.screens[zustand.teil] ?? null)
      : null;

  const teilnummer = zustand.teil;
  const ersatzfeld =
    kontaktbildschirm === null && schritt.type === "contact" && teilnummer !== null
      ? schritt.fields.find((f) => f.screen === teilnummer + 1)
      : undefined;

  const kicker = ersatzfeld ? schritt.question : null;
  const frage = kontaktbildschirm
    ? kontaktbildschirm.question
    : ersatzfeld
      ? kontaktBeschriftung(ersatzfeld)
      : schritt.type === "information"
        ? schritt.headline
        : schritt.question;
  const untertitel = kontaktbildschirm
    ? (kontaktbildschirm.description ?? null)
    : ersatzfeld
      ? (ersatzfeld.hint ?? schritt.description)
      : schritt.description;

  // ------------------------------------------------------------------
  // Bedienung, mit Meldung
  // ------------------------------------------------------------------

  const kachelfrage =
    (schritt.type === "single_choice" || schritt.type === "multiple_choice") &&
    schritt.layout === "grid";

  const bildschirm = bildschirmKennung(zustand.aktuell, zustand.teil);

  const antwortGeben = (neu: unknown) => {
    const vorher = zustand.antworten[schritt.id];
    steuerung.antworten(neu);

    /**
     * Eine geänderte Antwort ist etwas anderes als eine erste Antwort. Wer
     * eine Auswahl zurücknimmt, war unsicher, und das ist ein Hinweis auf eine
     * unklar formulierte Frage.
     */
    const geaendert =
      vorher !== undefined && JSON.stringify(vorher) !== JSON.stringify(neu);
    tracker.melden(geaendert ? "option_changed" : "answer_selected", {
      stepId: bildschirm,
      timeOnStepMs: tracker.zeitAufSchritt(),
    });
  };

  const weiterGehen = () => {
    const vorher = bildschirm;
    const geschafft = steuerung.weiter();

    if (geschafft) {
      tracker.melden("step_forward", { stepId: vorher });
      if (schritt.type === "contact" && zustand.teil === 0) {
        // Der Übergang in die Kontaktdaten ist die Stelle mit der höchsten
        // Absprungrate. Sie bekommt ein eigenes Ereignis, damit sie sich ohne
        // Umweg über die Schrittkennungen auswerten lässt.
        tracker.melden("contact_started", { stepId: schritt.id });
      }
      return;
    }

    tracker.melden("validation_error", {
      stepId: vorher,
      timeOnStepMs: tracker.zeitAufSchritt(),
    });
  };

  /**
   * Auch das automatische Weitergehen läuft hier durch.
   *
   * Ohne diese Zeile meldet keine Einfachauswahl ein step_forward, denn der
   * Zeitgeber ruft nicht den Knopf auf, sondern direkt die Steuerung. In der
   * Auswertung sähe damit fast jeder Bildschirm des Funnels wie ein
   * vollständiger Absprung aus, obwohl alle weitergegangen sind.
   */
  weiterRef.current = weiterGehen;

  const zurueckGehen = () => {
    tracker.melden("step_back", {
      stepId: bildschirm,
      timeOnStepMs: tracker.zeitAufSchritt(),
    });
    steuerung.zurueck();
  };

  zurueckRef.current = zurueckGehen;

  return (
    <div class="f-wurzel" style={stil} ref={wurzel}>
      <header class="f-kopf">
        {/*
          Auf dem ersten Schritt gibt es nichts, wohin zurück führen würde -
          dort stand bisher ein abgeblendeter Pfeil. Ein Knopf, der nichts tut,
          ist schlimmer als keiner: Wer ihn antippt und nichts passiert, hält
          das Formular für kaputt.

          Der Platz bleibt trotzdem stehen. Verschwände der Pfeil ganz, spränge
          der Fortschrittsbalken beim zweiten Schritt nach rechts, und eine
          Anzeige, die sich beim Vorwärtsgehen verschiebt, liest sich wie ein
          Fehler.
        */}
        {steuerung.kannZurueck ? (
          <button
            type="button"
            class="f-zurueck"
            aria-label="Eine Frage zurück"
            onClick={zurueckGehen}
          >
            <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
              <path
                d="M15 5 8 12l7 7"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>
        ) : (
          <span class="f-zurueck f-zurueck--leer" aria-hidden="true" />
        )}

        {/*
          Die Zahl neben dem Balken.

          Ein Balken allein lässt offen, wie weit es noch ist - und wer das
          nicht weiß, rechnet mit dem Schlimmsten. "60 %" beantwortet die
          Frage in zwei Zeichen. Nach dem ersten Antippen steht dort schon ein
          zweistelliger Wert, und genau das ist der Eindruck, der trägt: Es
          geht schnell.
        */}
        {funnel.settings.progress.show ? (
          <div
            class="f-fortschritt"
            role="progressbar"
            aria-valuenow={prozent}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label="Fortschritt"
            aria-valuetext={fortschrittText}
          >
            <div class="f-fortschritt__spur">
              <div
                class="f-fortschritt__balken"
                style={{ width: `${String(prozent)}%` }}
              />
            </div>
            <span class="f-fortschritt__zahl">{fortschrittText}</span>
          </div>
        ) : (
          <div class="f-fortschritt f-fortschritt--leer" />
        )}

        {funnel.theme.logo_url ? (
          <img class="f-logo" src={funnel.theme.logo_url} alt="" />
        ) : (
          <span class="f-kopf__platz" />
        )}
      </header>

      <main class="f-buehne">
        {/*
          Kachelfragen brauchen mehr Breite als Fließtext.

          Eine Frage mit fünf Kacheln passt in 520 Pixel nur dreispaltig, und
          dann steht die vierte Kachel allein in der zweiten Reihe. Text bleibt
          schmal, weil lange Zeilen schlechter zu lesen sind; nur die Kacheln
          dürfen sich ausbreiten.
        */}
        <div
          class={`f-tafel f-tafel--${zustand.richtung === 1 ? "vor" : "zurueck"}${
            kachelfrage ? " f-tafel--weit" : ""
          }`}
          key={`${zustand.aktuell}:${String(zustand.teil ?? 0)}`}
        >
          {kicker ? <p class="f-kicker">{kicker}</p> : null}
          {/*
            h2 und nicht h1: Der Funnel ist ein Baustein auf einer fremden
            Seite, nicht die Seite selbst. Auf den Anfrage-Seiten trägt die
            Hauptüberschrift das Keyword ("Ihr Trockenbau-Projekt"); eine
            zweite h1 mit der aktuellen Frage würde sie verwässern - und die
            Frage wechselt bei jedem Schritt, taugt also ohnehin nicht als
            Überschrift der Seite.

            Für Screenreader ändert sich nichts: Der Fokus springt bei jedem
            Schritt hierher, und das ist es, was angesagt wird.
          */}
          {frage ? (
            <h2 class="f-frage" ref={ueberschrift} tabIndex={-1}>
              {frage}
            </h2>
          ) : null}
          {untertitel ? <p class="f-unterzeile">{untertitel}</p> : null}

          <SchrittAnsicht
            schritt={schritt}
            wert={zustand.antworten[schritt.id]}
            fehler={zustand.fehler}
            teil={zustand.teil}
            /*
              Die erste Frage des Funnels - nur fuers Messen.

              besucht[0] ist der Einstiegsschritt und bleibt es: Der Weg wird
              beim Zuruecklaufen von hinten gekuerzt, nie von vorn. Der
              Vergleich stimmt deshalb auch dann, wenn jemand zurueckgeht.
            */
            istStart={zustand.besucht[0] === schritt.id}
            onWert={antwortGeben}
            onWeiter={weiterGehen}
            onAutoWeiter={autoWeiter}
          />

          {/*
            Die Vertrauenspunkte, nur auf der ersten Frage.

            Dort zögert man, und dort stand bisher nichts davon: Auf den
            Anfrageseiten liegen dieselben Aussagen unter dem Quiz, auf dem
            Handy außerhalb des Bildschirms. Ab Frage zwei ist entschieden.

            Unter den Antworten und nicht darüber, auf Wunsch des Betreibers:
            Frage und Antworten stehen so direkt beieinander, die Punkte
            folgen als Beruhigung darunter.
          */}
          {zustand.besucht[0] === schritt.id && funnel.settings.trust_points.length > 0 ? (
            <ul class="f-vertrauen">
              {funnel.settings.trust_points.map((punkt) => (
                <li class="f-vertrauen__punkt" key={punkt}>
                  <svg viewBox="0 0 20 20" width="14" height="14" aria-hidden="true">
                    <path
                      d="m4 10.5 4 4 8-9"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2.4"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                  {punkt}
                </li>
              ))}
            </ul>
          ) : null}

          {schritt.help_text ? <p class="f-hilfe">{schritt.help_text}</p> : null}
        </div>
      </main>

      <footer class="f-fuss">
        {zustand.meldung ? (
          <p class="f-meldung" role="alert">
            {zustand.meldung}
          </p>
        ) : null}

        {knopfZeigen ? (
          <button
            type="button"
            class={`f-knopf${autoTraegt ? " f-knopf--leise" : ""}`}
            onClick={weiterGehen}
          >
            {knopfText}
            {knopfZusatz ? <span class="f-knopf__zusatz">{knopfZusatz}</span> : null}
          </button>
        ) : null}
      </footer>

      {/*
        Unsichtbares Feld für Automaten. Ein Mensch sieht und füllt es nie;
        was hier ankommt, wird auf dem Server abgewiesen. Bewusst mit einem
        harmlosen Namen und nicht per display:none versteckt, weil einfache
        Automaten genau darauf prüfen.
      */}
      {funnel.settings.spam.honeypot ? (
        <input
          ref={honigtopf}
          type="text"
          name="website"
          class="f-honigtopf"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
        />
      ) : null}
    </div>
  );
}
